import { useState } from "react";

import {
  Button,
  Colors,
  Icon,
  Menu,
  MenuDivider,
  MenuItem,
  Position,
} from "@blueprintjs/core";

import { Popover } from "@/components/helpers/ui/blueprint-overrides/Popover";
import { Tooltip } from "@/components/helpers/ui/blueprint-overrides/Tooltip";
import { Box, Flex } from "@/components/layout/flexbox";
import { ShareStatus } from "@/components/pieces/roleplays/sharing/scenario-share/constants";
import {
  SharedPillProps,
  SharedUserType,
  SharedGroupType,
} from "@/components/pieces/roleplays/sharing/scenario-share/types";
import Avatar from "@/components/pieces/users/Avatar";
import { MIDTEXT_COLOR } from "@/css/constants";
import { PermissionLevel } from "@/graphql";

// Constants
const TITLE_CSS = {
  fontSize: "14px",
  fontWeight: 500,
  color: "black",
};

const ICON_SIZE = 36;
const ICON_MARGIN_RIGHT = "12px";
const AVATAR_SIZE = 36;
const MENU_BUTTON_STYLE = {
  backgroundColor: "#0719560A",
  fontSize: 14,
};
const REMOVE_ACCESS_TEXT = "Remove access";

// User item component
function UserItem({
  user,
  isMe,
  isMarkedForRemoval,
}: {
  user: SharedUserType;
  isMe: boolean;
  isMarkedForRemoval: boolean;
}) {
  return (
    <Box css={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Tooltip content={user?.name || user?.email}>
        <Avatar user={user} size={AVATAR_SIZE} />
      </Tooltip>
      <Flex style={{ gap: 8 }} flexDirection={"column"}>
        {user?.name && (
          <Box
            css={{
              ...TITLE_CSS,
              textDecoration: isMarkedForRemoval ? "line-through" : "none",
            }}
            lineHeight={"14px"}
          >
            {user?.name} {isMe && `(You)`}
          </Box>
        )}
        <Box fontSize={12} color={MIDTEXT_COLOR} lineHeight={"12px"}>
          {user?.email}
        </Box>
      </Flex>
    </Box>
  );
}

// Group item component
function GroupItem({
  group,
  isMarkedForRemoval,
}: {
  group: SharedGroupType;
  isMarkedForRemoval: boolean;
}) {
  return (
    <Flex alignItems="center">
      <Icon
        icon="people"
        color={Colors.GRAY3}
        size={ICON_SIZE}
        css={{
          marginRight: ICON_MARGIN_RIGHT,
        }}
      />
      <Box
        css={{
          ...TITLE_CSS,
          textDecoration: isMarkedForRemoval ? "line-through" : "none",
        }}
        lineHeight={"14px"}
      >
        {group.name} {group.workspace && `(${group.workspace.name})`}
      </Box>
    </Flex>
  );
}

export function SharedPill({
  item,
  isGroup,
  isMe,
  isOwner,
  shareStatus,
  onUpdateShareStatus,
}: SharedPillProps) {
  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [isMarkedForRemoval, setIsMarkedForRemoval] = useState(
    shareStatus === ShareStatus.Remove,
  );

  const handlePermissionChange = (permission: PermissionLevel) => {
    onUpdateShareStatus(permission);
    setIsOpenMenu(false);
  };

  const handleRemoveAccess = () => {
    setIsMarkedForRemoval(true);
    onUpdateShareStatus(ShareStatus.Remove);
    setIsOpenMenu(false);
  };

  const handleOpenMenu = () => {
    setIsOpenMenu(true);
  };

  const handleCloseMenu = () => {
    setIsOpenMenu(false);
  };

  const menuContent = (
    <Menu>
      <MenuItem
        text={ShareStatus[PermissionLevel.View]}
        onClick={() => handlePermissionChange(PermissionLevel.View)}
      />
      <MenuItem
        text={ShareStatus[PermissionLevel.Share]}
        onClick={() => handlePermissionChange(PermissionLevel.Share)}
      />

      <MenuDivider />

      <MenuItem
        text={REMOVE_ACCESS_TEXT}
        intent="danger"
        onClick={handleRemoveAccess}
      />
    </Menu>
  );

  const renderSharedItem = () => {
    if (isGroup) {
      const groupItem = item as SharedGroupType;
      return (
        <GroupItem group={groupItem} isMarkedForRemoval={isMarkedForRemoval} />
      );
    } else {
      const userItem = item as SharedUserType;
      return (
        <UserItem
          user={userItem}
          isMe={isMe}
          isMarkedForRemoval={isMarkedForRemoval}
        />
      );
    }
  };

  return (
    <Flex alignItems="center" justifyContent="space-between" py={"12px"}>
      {renderSharedItem()}

      <Flex justifyContent={"center"} alignItems={"center"} style={{ gap: 4 }}>
        <Box css={TITLE_CSS}>
          {isOwner ? (
            "Owner"
          ) : (
            <Popover
              content={menuContent}
              minimal={true}
              canEscapeKeyClose={true}
              position={Position.BOTTOM_LEFT}
              usePortal={true}
              onClose={handleCloseMenu}
              isOpen={isOpenMenu}
            >
              <Button
                style={MENU_BUTTON_STYLE}
                onClick={handleOpenMenu}
                rightIcon="caret-down"
                text={ShareStatus[shareStatus] || shareStatus}
              />
            </Popover>
          )}
        </Box>
      </Flex>
    </Flex>
  );
}
